import React, { useMemo, useState } from 'react';
import dayjs from 'dayjs';
import EntityNotesDrawer, { ReminderRecord } from '../Notes/EntityNotesDrawer';
import ReminderDrawer, { ReminderFormValues } from '../Reminders/ReminderDrawer';
import ReminderService, { ReminderPayload, ReminderStateType } from '../../services/ReminderService';
import { openNotification } from '../../utils';

type ProposalProcessHistoryTitleMode = 'proposal-list' | 'follow-up';

type StatusOption = {
  id?: number | string;
  name?: string;
  title?: string;
  color?: string;
};

interface ProposalProcessHistoryDrawerProps {
  open: boolean;
  proposal: any | null;
  isAdmin: boolean;
  statuses?: StatusOption[];
  titleMode?: ProposalProcessHistoryTitleMode;
  onClose: () => void;
}

const getProposalId = (proposal: any) => {
  const proposalId = Number(proposal?.id);
  return Number.isNaN(proposalId) ? undefined : proposalId;
};

const getCompanyId = (proposal: any) => {
  const companyId = Number(proposal?.company?.id);
  return Number.isNaN(companyId) ? undefined : companyId;
};

const getProposalLabel = (proposal: any) => {
  const proposalId = getProposalId(proposal);
  return proposal?.proposalNumber || proposal?.proposalCode || proposalId?.toString();
};

const getCompanyName = (proposal: any) => {
  const companyId = getCompanyId(proposal);
  return proposal?.company?.name || proposal?.company?.title || companyId?.toString();
};

const getReminderMaxDate = (statusId?: number) => {
  if (statusId === 3) {
    return dayjs().add(21, 'day').endOf('day');
  }
  if (statusId === 12) {
    return dayjs().add(51, 'day').endOf('day');
  }
  return undefined;
};

const ProposalProcessHistoryDrawer: React.FC<ProposalProcessHistoryDrawerProps> = ({
  open,
  proposal,
  isAdmin,
  statuses = [],
  titleMode = 'follow-up',
  onClose,
}) => {
  const [reminderDrawerOpen, setReminderDrawerOpen] = useState(false);
  const [reminderInitialValues, setReminderInitialValues] = useState<Partial<ReminderFormValues> | undefined>(undefined);
  const [reminderMaxDate, setReminderMaxDate] = useState<dayjs.Dayjs | undefined>(undefined);
  const [reminderCompanyName, setReminderCompanyName] = useState<string | undefined>(undefined);
  const [reminderProposalLabel, setReminderProposalLabel] = useState<string | undefined>(undefined);
  const [reminderProposals, setReminderProposals] = useState<any[]>([]);
  const [remindersVersion, setRemindersVersion] = useState(0);
  const [reminderEditTarget, setReminderEditTarget] = useState<any | null>(null);

  const proposalId = getProposalId(proposal);
  const companyId = getCompanyId(proposal);
  const proposalLabel = getProposalLabel(proposal);
  const companyName = getCompanyName(proposal);

  const statusInfo = useMemo(() => {
    const statusId = proposal?.status?.id ?? proposal?.statusId;
    return statuses.find((status) => String(status.id) === String(statusId)) ?? proposal?.status;
  }, [proposal, statuses]);

  const title = useMemo(() => {
    if (titleMode === 'proposal-list' || titleMode === 'follow-up') {
      const titleParts = [companyName, proposalLabel || proposalId].filter(Boolean);
      return titleParts.length ? `İşlem Geçmişi - ${titleParts.join(' - ')}` : 'İşlem Geçmişi';
    }
    return proposalLabel ? `İşlem Geçmişi - ${proposalLabel}` : 'İşlem Geçmişi';
  }, [companyName, proposalId, proposalLabel, titleMode]);

  const buildReminderProposalList = (sourceProposal: any, sourceProposalId?: number, sourceCompanyId?: number) => [
    {
      id: sourceProposalId,
      proposalNumber: sourceProposal?.proposalNumber || sourceProposal?.proposalCode || sourceProposalId,
      proposalCode: sourceProposal?.proposalCode,
      createdAt: sourceProposal?.createdAt,
      companyId: sourceCompanyId,
      statusId: sourceProposal?.statusId,
      status: sourceProposal?.status,
    },
  ];

  const openReminderDrawerForProposal = (sourceProposal: any) => {
    setReminderEditTarget(null);
    const sourceProposalId = getProposalId(sourceProposal);
    const sourceCompanyId = getCompanyId(sourceProposal);
    const sourceProposalLabel = getProposalLabel(sourceProposal);
    setReminderInitialValues({
      activityType: 'CALL',
      entityTab: 'PROPOSAL',
      proposalId: sourceProposalId,
      proposalCompanyId: sourceCompanyId,
      state: 'PENDING',
      statusId: sourceProposal?.status?.id ?? sourceProposal?.statusId,
    });
    setReminderCompanyName(getCompanyName(sourceProposal));
    setReminderProposalLabel(sourceProposalLabel);
    setReminderProposals(buildReminderProposalList(sourceProposal, sourceProposalId, sourceCompanyId));
    setReminderMaxDate(getReminderMaxDate(sourceProposal?.statusId));
    setReminderDrawerOpen(true);
  };

  const handleCreateReminderFromNotes = () => {
    if (!proposal) {
      openNotification('error', 'Hata', 'Teklif bilgisi bulunamadı.');
      return;
    }
    openReminderDrawerForProposal(proposal);
  };

  const handleEditReminder = async (reminder: ReminderRecord) => {
    if (!isAdmin) {
      openNotification('warning', 'Uyarı', 'Görev güncelleme sadece admin kullanıcılar için uygundur.');
      return;
    }
    if (!proposal) {
      openNotification('error', 'Hata', 'Teklif bilgisi bulunamadı.');
      return;
    }
    try {
      const response: any = await ReminderService.getReminder(reminder.id);
      const record = response?.data ?? response ?? reminder;
      setReminderInitialValues({
        activityType: record.activityType || 'TASK',
        entityTab: 'PROPOSAL',
        proposalId,
        proposalCompanyId: companyId,
        reminderDate: record.reminderDate ? dayjs(record.reminderDate) : undefined,
        dueDate: record.dueDate ? dayjs(record.dueDate) : undefined,
        estimatedDurationMinutes: record.estimatedDurationMinutes,
        content: record.content,
        channel: record.channel,
        link: record.link,
        state:
          (record.state as ReminderStateType) ??
          (typeof record.status === 'string' ? record.status : undefined),
        statusId:
          record.statusId ??
          (record.status && typeof record.status === 'object' ? record.status.id : undefined),
        result: (() => {
          if (record.result) return record.result;
          const raw = record.properties;
          if (!raw) return undefined;
          if (typeof raw === 'object') return (raw as any)?.result;
          if (typeof raw === 'string') {
            try {
              const parsed = JSON.parse(raw);
              return parsed?.result;
            } catch {
              return undefined;
            }
          }
          return undefined;
        })(),
        properties: record.properties,
        accountId: (() => {
          const val = record.accountId ?? record?.account?.id;
          const num = Number(val);
          return Number.isNaN(num) ? undefined : num;
        })(),
      });
      setReminderCompanyName(companyName);
      setReminderProposalLabel(proposalLabel);
      setReminderProposals(buildReminderProposalList(proposal, proposalId, companyId));
      setReminderMaxDate(getReminderMaxDate(proposal?.statusId));
      setReminderEditTarget(record);
      setReminderDrawerOpen(true);
    } catch (error) {
      openNotification('error', 'Hata', 'Görev bilgileri yüklenirken hata oluştu.');
      console.error(error);
    }
  };

  const closeReminderDrawer = () => {
    setReminderDrawerOpen(false);
    setReminderInitialValues(undefined);
    setReminderEditTarget(null);
    setReminderMaxDate(undefined);
    setReminderCompanyName(undefined);
    setReminderProposalLabel(undefined);
    setReminderProposals([]);
  };

  const handleReminderSubmit = async (values: ReminderFormValues) => {
    try {
      const entityId = values.proposalId;
      const isMeeting = values.activityType === 'MEETING_OFFLINE' || values.activityType === 'MEETING_ONLINE';
      const channelDefault =
        values.activityType === 'MEETING_OFFLINE'
          ? 'IN_PERSON'
          : values.activityType === 'MEETING_ONLINE'
            ? 'ONLINE'
            : '';
      const resolvedChannel = values.channel ?? (isMeeting ? channelDefault : undefined);
      const reminderDate = values.reminderDate ? values.reminderDate.format('YYYY-MM-DDTHH:mm:ss') : undefined;
      const dueDate = values.dueDate ? values.dueDate.format('YYYY-MM-DDTHH:mm:ss') : undefined;
      const resolvedTargetState =
        (reminderEditTarget?.state as ReminderStateType) ??
        (typeof reminderEditTarget?.status === 'string' ? reminderEditTarget.status : undefined);
      const finalState: ReminderStateType =
        (values.state as ReminderStateType) ?? resolvedTargetState ?? 'PENDING';
      const finalStatusId =
        values.statusId ??
        reminderEditTarget?.statusId ??
        (reminderEditTarget?.status && typeof reminderEditTarget.status === 'object'
          ? reminderEditTarget.status.id
          : undefined);
      const properties =
        values.properties && typeof values.properties === 'object'
          ? JSON.stringify(values.properties)
          : values.properties;
      const duplicateContent = values.duplicateContent || values.content;
      const duplicateReminderDate = values.duplicateReminderDate || values.reminderDate;
      const duplicateReminderDateText = duplicateReminderDate
        ? duplicateReminderDate.format('YYYY-MM-DDTHH:mm:ss')
        : undefined;
      const payload: ReminderPayload = {
        content: values.content,
        reminderDate,
        dueDate,
        estimatedDurationMinutes: values.estimatedDurationMinutes,
        activityType: values.activityType,
        channel: resolvedChannel,
        link: isMeeting ? values.link : undefined,
        state: finalState,
        ...(finalStatusId ? { statusId: finalStatusId } : {}),
        result: values.result,
        ...(properties ? { properties } : {}),
        ...(entityId ? { entityName: 'PROPOSAL' as const, entityId } : {}),
        ...(isAdmin && values.accountId ? { accountId: values.accountId } : {}),
      };

      if (reminderEditTarget) {
        await ReminderService.updateReminder(reminderEditTarget.id, payload);
        if (finalState === 'COMPLETED' && values.duplicateOnSave) {
          const cleanedProperties = (() => {
            if (!properties) return undefined;
            try {
              const parsed = typeof properties === 'string' ? JSON.parse(properties) : properties;
              if (!parsed || typeof parsed !== 'object') return undefined;
              const { result, resultImages, resultDocuments, ...rest } = parsed as any;
              return Object.keys(rest).length ? JSON.stringify(rest) : undefined;
            } catch {
              return undefined;
            }
          })();
          const duplicatePayload: ReminderPayload = {
            ...payload,
            content: duplicateContent,
            reminderDate: duplicateReminderDateText,
            state: 'PENDING',
            result: undefined,
            ...(cleanedProperties ? { properties: cleanedProperties } : {}),
          };
          if (isAdmin) {
            const targetAccountId =
              values.accountId ?? reminderEditTarget.accountId ?? reminderEditTarget?.account?.id;
            await ReminderService.createForAccount({ ...duplicatePayload, accountId: targetAccountId });
          } else {
            await ReminderService.createForMe(duplicatePayload);
          }
        }
        openNotification('success', 'Başarılı', 'Görev güncellendi');
      } else if (isAdmin) {
        await ReminderService.createForAccount({ ...payload, accountId: values.accountId });
        openNotification('success', 'Başarılı', 'Görev oluşturuldu');
      } else {
        await ReminderService.createForMe(payload);
        openNotification('success', 'Başarılı', 'Görev oluşturuldu');
      }

      closeReminderDrawer();
      setRemindersVersion((prev) => prev + 1);
    } catch (e: any) {
      if (e?.errorFields) return;
      openNotification(
        'error',
        'Hata',
        e?.response?.data || (reminderEditTarget ? 'Görev güncellenemedi' : 'Görev oluşturulamadı'),
      );
    }
  };

  const handleClose = () => {
    closeReminderDrawer();
    onClose();
  };

  return (
    <>
      <EntityNotesDrawer
        open={open}
        entityId={proposalId ?? null}
        entityName="proposal"
        title={title}
        proposalStatusId={proposal?.statusId ?? proposal?.status?.id ?? null}
        proposalStatusName={statusInfo?.name || statusInfo?.title || null}
        proposalStatusColor={statusInfo?.color || null}
        proposalCreatedAt={proposal?.createdAt ?? null}
        proposalReferenceId={proposal?.referenceId ?? null}
        onClose={handleClose}
        onAddReminder={handleCreateReminderFromNotes}
        onEditReminder={handleEditReminder}
        remindersVersion={remindersVersion}
      />

      <ReminderDrawer
        open={reminderDrawerOpen}
        isEdit={Boolean(reminderEditTarget)}
        isAdmin={isAdmin}
        companies={[]}
        proposals={reminderProposals}
        initialValues={reminderInitialValues}
        hideEntityTabs
        fixedCompanyId={reminderInitialValues?.proposalCompanyId}
        fixedCompanyName={reminderCompanyName}
        fixedProposalId={reminderInitialValues?.proposalId}
        fixedProposalLabel={reminderProposalLabel}
        maxReminderDate={reminderMaxDate}
        onClose={closeReminderDrawer}
        onSubmit={handleReminderSubmit}
      />
    </>
  );
};

export default ProposalProcessHistoryDrawer;
